import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, FormArray} from '@angular/forms';
import {
  BaseTask,
  CaseSettings,
  ClientSettings,
  ConfirmDialogModel,
  County,
  CountyFeeSetting,
  DocumentGenerationEntryForm,
  EvictionUpdateConfirmationMessage,
  StepCompletionModel,
  Tenant,
  User,
  WorkflowStep
} from '@ee/common/models';
import {lastValueFrom, Subscription} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {
  CaseTaskEditorType,
  CaseTaskOperation,
  FlatFeeType,
  TaskType
} from '@ee/common/enums';
import {CurrencyPipe} from '@angular/common';
import {ClientService, CountyFeeSettingsService, DocumentGenerationSelectionService} from '@ee/common/services';
import {generateCourtTaskForm, ToStepCompletionPayload} from '@ee/common/forms';
import {generateCountyFeeBillable, generateWritFeeBillable, generateSheriffFeeBillable, generateHearingFeeBillable, GenerateStepCompletedForm} from './step-completed.form';
import {CaseTaskEditorDialogComponent, CourtJudgementComponent} from '@ee/common/tasks';
import {MatSnackBar} from '@angular/material/snack-bar';
import {CdkDragDrop} from '@angular/cdk/drag-drop';

@Component({
  selector: 'ee-complete-workflow-step',
  template: `
    @if (form) {
      <div class="main-step-wrapper" [formGroup]="form">
        @if (!hideLabel || !workflowStepTask || showScheduleNewCourtDateButton) {
          <div
            class="mb-4 label flex flex-col md:flex-row justify-stretch items-stretch md:justify-end md:items-center">
            @if (!hideLabel) {
              <div class="md:flex-1">Fill out the form and submit to complete the current step.</div>
            }
            @if ((!workflowStepTask && showWorkflowStepScheduler) || showScheduleNewCourtDateButton) {
              <div>
                @if (!workflowStepTask && showWorkflowStepScheduler && planAllowsCaseTasks) {
                  <ee-workflow-task-info
                  [users]="userSuggestions" (onSave)="onSaveTask.emit($event)"></ee-workflow-task-info>
                }
                @if (showScheduleNewCourtDateButton) {
                  <a class="ee__link font-medium text-sm" (click)="addCourtDateForm()">
                    Schedule New Court Date
                  </a>
                }
              </div>
            }
          </div>
        }
        <div class="main-step" [ngClass]="{'vertical': forceVertical}">
          <div class="step-completion-fields">
            @if (customFieldsArray?.length) {
              <div class="custom-fields mb-2">
                @for (fieldForm of customFieldsArray?.controls; track fieldForm) {
                  <ee-custom-field class="mb-2"
                  [form]="$any(fieldForm)"></ee-custom-field>
                }
              </div>
            }
            @if (hasBillableItems) {
              <div class="highlighted-box mb-4">
                <div class="highlighted-box-label">
                  <div class="caps-label">Auto-Billables</div>
                  <div class="billables-total">{{ billablesTotalDisplay }} total</div>
                </div>
                <ee-workflow-step-billables
                  [billableItemsArray]="billableItemsArray"
                  [workflowStep]="this.nextStep">
                </ee-workflow-step-billables>
              </div>
            }
            @if (hasDocumentTemplates) {
              <div class="highlighted-box mb-4 generate-documents-box">
                <div class="highlighted-box-label">Generate Documents</div>
                <ee-document-generation-control mode="selection"
                  [entries]="documentGenerationArray"/>
              </div>
            }
            @if ((!hideComments || !hideNotes) && (form.get('comment') || form.get('notes'))) {
              <div
                class="flex flex-col justify-stretch items-stretch">
                @if (!hideComments && form.get('comment')) {
                  <mat-form-field
                    class="mb-4 compact" appearance="outline">
                    <mat-label>Save a message for the client</mat-label>
                    <textarea matInput rows="2" formControlName="comment"></textarea>
                  </mat-form-field>
                }
                @if (!hideNotes && form.get('notes')) {
                  <mat-form-field
                    appearance="outline" class="compact">
                    <mat-label>Save a note visible only to your firm</mat-label>
                    <textarea matInput rows="2" formControlName="notes"></textarea>
                  </mat-form-field>
                }
              </div>
            }
          </div>
          <div class="workflow-generators">
            @if (!!workflowStepTask && planAllowsCaseTasks) {
              <ee-workflow-task class="mb-4" [task]="workflowStepTask" [users]="userSuggestions" (onDelete)="onDeleteTask.emit($event)"
              (onUpdateTask)="onUpdateTask.emit($event)"></ee-workflow-task>
            }
            @if (hasJudgementForm) {
              <div class="highlighted-box mb-4">
                <div class="highlighted-box-label">
                  <div>Judgment: {{ judgementResultForm.get('name').value }}</div>
                  <div>
                    {{ judgementResultForm.get('due_date').value | dateTimeFromIso | dateTimeToFormat:'MMM d, yyyy' }}
                    @if (judgementResultForm.get('legacy').value && !!judgementResultForm.get('legacy_court_time').value) {
                      <span class="ml-1">
                        {{ judgementResultForm.get('legacy_court_time').value }}
                      </span>
                    }
                    @if (!!judgementResultForm.get('show_time').value && !judgementResultForm.get('legacy').value) {
                      <span class="ml-1">
                        {{ judgementResultForm.get('due_date').value | dateTimeFromIso | dateTimeToFormat:'t' }}
                      </span>
                    }
                  </div>
                </div>
                <ee-court-judgement class="section-wrapper" [form]="judgementResultForm" [hideComment]="true"
                  [caseSettings]="caseSettings" [showFileUpload]="showFileUpload"
                  (uploadReady)="uploadReady.emit($event)"
                (uploadCleared)="uploadCleared.emit()"></ee-court-judgement>
              </div>
            }
            @if (hasCourtDateForm) {
              <div class="highlighted-box mb-4" [ngClass]="{'mb-4': !forceVertical && !hideTasks}">
                <div class="highlighted-box-label">
                  <span>New Court Task</span>
                  @if (showRemoveNewCourtDateButton) {
                    <span (click)="removeCourtDateForm()" class="remove">
                      Remove
                    </span>
                  }
                </div>
                <ee-task-form class="mb-2" [taskForm]="courtDateForm" [userSuggestions]="userSuggestions"></ee-task-form>
              </div>
            }
            @if (!hideTasks && planAllowsCaseTasks) {
              <div class="task-list">
                <div class="flex flex-row justify-between items-end mb-2 w-full">
                  <div class="task-list-label">Tasks</div>
                  <div class="task-list-description">
                    <!-- Toggle to show/hide tasks -->
                    @if (caseTasks?.length) {
                      <a class="ee__link font-medium text-sm" (click)="tasksDisplayToggle = !tasksDisplayToggle">
                        {{ tasksDisplayToggle ? '- Hide' : '+ Show' }} Tasks ({{ caseTasks?.length }})
                      </a>
                    }
                  </div>
                </div>
                @if (tasksDisplayToggle) {
                  @if (!!caseTasks?.length) {
                    <div class="task-list-wrapper" cdkDropList [cdkDropListDisabled]="isRestrictedCase" (cdkDropListDropped)="taskDrop($event)">
                      @for (task of caseTasks; track task) {
                        <ee-case-task [cdkDragDisabled]="isRestrictedCase" cdkDrag [task]="task" [users]="userSuggestions" [hideBorder]="true"
                        [completable]="!isRestrictedCase" [editable]="!isRestrictedCase"
                        (onMarkCompleted)="onTaskMarkedCompleted.emit(task.id)" (onEdit)="editTask(task)"></ee-case-task>
                      }
                      <div class="task-custom-placeholder" *cdkDragPlaceholder></div>
                      @if (updatingTaskList) {
                        <div class="updating-task-list">Updating...</div>
                      }
                    </div>
                  } @else {
                    <div class="font-medium text-center w-full flex items-center justify-center flex-1">No tasks have been added.</div>
                  }
                }
              </div>
            }
          </div>
        </div>
      </div>
    }
    `,
  styleUrls: ['./complete-workflow-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CompleteWorkflowStepComponent implements OnDestroy, OnChanges {
  evictionUpdateMessage: ConfirmDialogModel;
  hasDocumentTemplates = false;
  hasCourtDateForm = false;
  hasJudgementForm = false;
  hasBillableItems = false;
  updatingTaskList = false;
  form: UntypedFormGroup;
  tasksDisplayToggle = true;
  private subs: Subscription[] = [];
  private currencyPipe: CurrencyPipe;
  /** Re-armed on every rebuild — OnPush needs a nudge when values inside the (non-input) form
   *  change so header derivations like the billables total stay live. */
  private formChangesSub: Subscription | null = null;

  public message = ' ';

  @Input({required: true}) caseSettings: CaseSettings;
  @Input({required: true}) nextStep: WorkflowStep;
  @Input({required: true}) planAllowsCaseTasks = false;
  /**
   * The current case data the conditional rules evaluate against when pre-populating
   * the document override selector (Req 5.1). Typically the eviction record.
   */
  @Input({required: false}) caseData: any = {};
  @Input({required: true}) userSuggestions: User[] = [];
  @Input({required: false}) clientId: string | null;
  @Input({required: false}) caseId: string | null;
  @Input({required: false}) hideComments = false;
  @Input({required: false}) hideNotes = false;
  @Input({required: false}) nextCourtTask: BaseTask | null;
  @Input({required: false}) county: County | null;
  @Input({required: false}) tenants: Tenant[] = [];
  @Input({required: false}) countyFeeSetting: CountyFeeSetting;
  @Input({required: false}) parentForm: UntypedFormGroup | null;
  @Input({required: false}) hideLabel = false;
  @Input({required: false}) workflowStepTask: BaseTask | null;
  @Input({required: false}) caseTasks: BaseTask[] | null;
  @Input({required: false}) forceVertical = false;
  @Input({required: false}) hideTasks = false;
  @Input({required: false}) forceJudgementPlanCompletion = false;
  @Input({required: false}) forceAddCourtTaskButton = false;
  @Input({required: false}) showWorkflowStepScheduler = false;
  @Input({required: false}) documentTemplateVisibility: 'visible' | 'hidden' | 'default' = 'default';
  @Input({required: false}) billablesVisibility: 'visible' | 'hidden' | 'default' = 'default';
  /**
   * Whether the case is a restricted case (closed + archived client).
   * When true, disables task editing, deletion, completion, and reordering.
   * Requirements: 9.3
   */
  @Input({required: false}) isRestrictedCase = false;
  /**
   * Renders the "Judgment Document" control inside the judgment box, letting the scanned judgment be
   * attached in the same action that completes the step. Hosts gate this on
   * `Permission.CASE_UPLOAD_DOCUMENTS_CREATE`; the server re-asserts it when the write carries an id.
   * The control only appears when there is a court task to judge (the `hasJudgementForm` box).
   *
   * Purely presentational — deliberately NOT in [REBUILD_INPUTS], so a permission-driven change never
   * regenerates the form under the user.
   */
  @Input({required: false}) showFileUpload = false;
  @Output() onSave = new EventEmitter<StepCompletionModel>();
  /**
   * Re-emitted from the embedded judgment form so the host can track the staged `upload_id` and stamp
   * it onto `judgement_result.judgment_upload_id` at submit time — the same single-call mechanism the
   * court-task completion path uses.
   */
  @Output() public uploadReady = new EventEmitter<{ upload_id: string; original_format: string }>();
  @Output() public uploadCleared = new EventEmitter<void>();

  @Output() public onDeleteTask = new EventEmitter<BaseTask>();
  @Output() public onUpdateTask = new EventEmitter<BaseTask>();
  @Output() public onTaskMarkedCompleted = new EventEmitter<string>();
  @Output() public onSaveTask = new EventEmitter<BaseTask>();
  @Output() public onUpdateTaskOrder = new EventEmitter<{ [key: string]: number }>();

  @ViewChild(CourtJudgementComponent) private courtJudgement?: CourtJudgementComponent;

  /**
   * Clears any staged judgment document — aborts an in-flight upload, drops the staged temp object and
   * resets the file tile. The child's own reset emits `uploadCleared`, which propagates to the host so
   * its tracked upload id clears in lock-step, leaving no "attached but untracked" phantom state.
   *
   * Hosts call this after a submit and whenever they swap away from this form, so a retry can never
   * send a stale id while the UI still shows a file.
   */
  public clearJudgmentUpload(): void {
    this.courtJudgement?.clearUpload();
  }

  constructor(private fb: UntypedFormBuilder,
              public dialog: MatDialog,
              public clientService: ClientService,
              public countyFeeSettingsService: CountyFeeSettingsService,
              public snackBar: MatSnackBar,
              public documentGenerationSelection: DocumentGenerationSelectionService,
              public changeDetectorRef: ChangeDetectorRef) {
    this.evictionUpdateMessage = EvictionUpdateConfirmationMessage();
    this.currencyPipe = new CurrencyPipe('en-US');
  }

  /** Running total shown in the Auto-Billables box header. Evaluated per CD pass; the
   *  form.valueChanges subscription in [rebuildForm] marks for check when amounts change. */
  get billablesTotalDisplay(): string {
    const total = (this.billableItemsArray?.controls ?? [])
      .reduce((sum, control) => sum + (Number(control.get('amount')?.value) || 0), 0);
    return this.currencyPipe.transform(total, 'USD', 'symbol', '1.2-2');
  }

  /**
   * Inputs whose change invalidates the generated step-completion form. `county` is deliberately
   * NOT in this list — when it changes without any of these, the county-derived billables are
   * replaced inside the EXISTING form (preserving everything else the user may have edited).
   * Task inputs (caseTasks/workflowStepTask) and presentational inputs (userSuggestions, hide*,
   * forceVertical, isRestrictedCase, …) never rebuild: the template reads them directly.
   */
  private static readonly REBUILD_INPUTS: readonly string[] = [
    'nextStep', 'clientId', 'tenants', 'countyFeeSetting', 'nextCourtTask', 'caseSettings',
    'forceJudgementPlanCompletion', 'caseData', 'documentTemplateVisibility', 'billablesVisibility',
    'parentForm'
  ];

  /** Pending-work flags + drain loop: async refreshes are SERIALIZED (Angular doesn't await an
   *  async ngOnChanges, so without this two rapid input changes interleave and a stale response
   *  can apply over a newer one). New changes arriving mid-run just set flags; the running loop
   *  picks them up and always works from the LIVE input values, so the final state converges on
   *  the latest inputs. A rebuild subsumes a pending county patch (regeneration recomputes the
   *  county billables anyway). */
  private pendingRebuild = false;
  private pendingCountyPatch = false;
  private refreshing = false;

  /** Client settings held for [clientSettingsForClientId] — re-fetched only when the client
   *  actually changes (the county-fee fetch below has the equivalent guard by county id). */
  private clientSettings: ClientSettings | null = null;
  private clientSettingsForClientId: string | null;

  private static inputChanged(change: SimpleChange | undefined): boolean {
    return !!change && (change.firstChange || change.currentValue !== change.previousValue);
  }

  ngOnChanges(changes: SimpleChanges): Promise<void> {
    this.tasksDisplayToggle = true;
    if (changes['caseTasks'] || changes['workflowStepTask']) {
      // Task data refreshed — the template re-reads these inputs directly; never a rebuild.
      this.updatingTaskList = false;
    }

    const needsRebuild = !this.form ||
      CompleteWorkflowStepComponent.REBUILD_INPUTS.some(key =>
        CompleteWorkflowStepComponent.inputChanged(changes[key]));
    if (needsRebuild) {
      this.pendingRebuild = true;
    } else if (CompleteWorkflowStepComponent.inputChanged(changes['county'])) {
      this.pendingCountyPatch = true;
    }
    return this.drainRefreshQueue();
  }

  private async drainRefreshQueue(): Promise<void> {
    if (this.refreshing) {
      return; // the running loop will pick up the newly-set flags
    }
    this.refreshing = true;
    try {
      while (this.pendingRebuild || this.pendingCountyPatch) {
        if (this.pendingRebuild) {
          this.pendingRebuild = false;
          this.pendingCountyPatch = false; // subsumed — the rebuild recomputes county billables
          await this.rebuildForm();
        } else {
          this.pendingCountyPatch = false;
          await this.replaceCountyBillableItem(this.nextStep, this.county);
        }
      }
    } finally {
      this.refreshing = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  /** Regenerates the step-completion form from the LIVE input values, carrying the user's typed
   *  edits (comment/notes/custom fields) across — see [captureUserEdits]. */
  private async rebuildForm(): Promise<void> {
    this.hasDocumentTemplates = false;
    this.hasCourtDateForm = false;
    this.hasJudgementForm = false;
    this.hasBillableItems = false;

    if (this.county && this.countyFeeSetting?.county_id !== this.county?.id) {
      try {
        this.countyFeeSetting = await lastValueFrom(this.countyFeeSettingsService.getByCounty(this.county));
      } catch {
        // County fee lookup failed — generate the form without county-derived amounts (0 defaults)
      }
    }

    if (this.clientSettingsForClientId !== this.clientId) {
      try {
        this.clientSettings = await lastValueFrom(this.clientService.getClientSettings(this.clientId));
        this.clientSettingsForClientId = this.clientId;
      } catch {
        this.clientSettings = null; // fetch retried on the next rebuild (id not recorded)
      }
    }

    const userEdits = this.captureUserEdits(this.form);
    this.form = GenerateStepCompletedForm(this.fb, this.nextStep, this.county, this.tenants ?? [], this.countyFeeSetting,
      this.clientSettings, this.nextCourtTask, this.forceJudgementPlanCompletion, this.caseSettings);
    this.restoreUserEdits(this.form, userEdits);
    this.formChangesSub?.unsubscribe();
    this.formChangesSub = this.form.valueChanges.subscribe(() => this.changeDetectorRef.markForCheck());
    if (this.parentForm) {
      this.parentForm.setControl('step_completion_details', this.form);
    }
    this.hasDocumentTemplates = (this.nextStep?.document_generation?.length > 0 ||
        this.documentTemplateVisibility === 'visible') &&
      this.documentTemplateVisibility !== 'hidden';
    // Pre-populate the override selector with the rule-evaluated default set: each
    // entry whose conditional_rule evaluates true (or has no rule) against the case
    // data, carrying its hide_from_client value (Req 5.1–5.6).
    this.documentGenerationSelection.seedDefaultSelection(
      this.documentGenerationArray, this.nextStep?.document_generation, this.buildRuleContext(this.caseData));
    this.hasBillableItems = (this.nextStep?.billable_items?.length > 0 || this.billablesVisibility === 'visible') &&
      this.billablesVisibility !== 'hidden';
    this.hasCourtDateForm = this.nextStep?.show_new_court_date_form && !!this.courtDateForm;
    this.hasJudgementForm = !!this.nextCourtTask && !this.nextCourtTask?.completed_date && !!this.judgementResultForm;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Values the user actually typed (dirty controls) that survive a rebuild: comment, notes, and
   * custom-field values (matched by their stable `field` key). Billables are intentionally NOT
   * preserved — they are derived from the step/county/tenants/client settings, and a rebuild means
   * one of those changed; carrying user-edited amounts forward would keep stale fees.
   */
  private captureUserEdits(form: UntypedFormGroup | undefined):
    { comment?: string; notes?: string; customFieldValues: Map<string, unknown> } | null {
    if (!form) {
      return null;
    }
    const customFieldValues = new Map<string, unknown>();
    (form.get('custom_fields') as UntypedFormArray)?.controls.forEach(control => {
      const key = control.get('field')?.value;
      const valueControl = control.get('value');
      if (key && valueControl?.dirty) {
        customFieldValues.set(key, valueControl.value);
      }
    });
    return {
      comment: form.get('comment')?.dirty ? form.get('comment').value : undefined,
      notes: form.get('notes')?.dirty ? form.get('notes').value : undefined,
      customFieldValues
    };
  }

  private restoreUserEdits(form: UntypedFormGroup,
    edits: { comment?: string; notes?: string; customFieldValues: Map<string, unknown> } | null): void {
    if (!edits) {
      return;
    }
    const restore = (controlName: 'comment' | 'notes', value: string | undefined) => {
      const control = form.get(controlName);
      if (control && value !== undefined) {
        control.setValue(value);
        control.markAsDirty();
      }
    };
    restore('comment', edits.comment);
    restore('notes', edits.notes);
    (form.get('custom_fields') as UntypedFormArray)?.controls.forEach(control => {
      const key = control.get('field')?.value;
      if (key && edits.customFieldValues.has(key)) {
        const valueControl = control.get('value');
        valueControl.setValue(edits.customFieldValues.get(key));
        valueControl.markAsDirty();
      }
    });
  }

  get customFieldsArray(): UntypedFormArray {
    return this.form?.get('custom_fields') as UntypedFormArray;
  }

  /**
   * Flattens the case record into the shape the conditional-rule evaluator resolves field paths
   * against. Built-in fields already sit at the top level of the eviction; custom and internal
   * fields are stored under the `custom_fields` / `internal_fields` maps but rules reference them
   * by their bare field key. Spreading those maps to the top level lets a rule on a custom/internal
   * field (e.g. "Precinct", "Place") resolve, mirroring the backend's flat `case.data` context.
   */
  private buildRuleContext(caseData: any): Record<string, any> {
    if (!caseData || typeof caseData !== 'object') {
      return {};
    }
    return {
      ...caseData,
      ...(caseData.internal_fields ?? {}),
      ...(caseData.custom_fields ?? {})
    };
  }

  get documentGenerationArray(): FormArray<DocumentGenerationEntryForm> {
    return this.form?.get('document_generation') as FormArray<DocumentGenerationEntryForm>;
  }

  get billableItemsArray(): UntypedFormArray {
    return this.form?.get('billable_items') as UntypedFormArray;
  }

  get courtDateForm(): UntypedFormGroup | null {
    return this.form?.get('new_court_task') as UntypedFormGroup | null;
  }

  get judgementResultForm(): UntypedFormGroup | null {
    return this.form?.get('judgement_result') as UntypedFormGroup | null;
  }

  get showScheduleNewCourtDateButton(): boolean {
    return (this.forceAddCourtTaskButton || this.nextStep?.show_new_court_date_form) && !this.nextStep?.new_court_date_required && !this.hasCourtDateForm;
  }

  get showRemoveNewCourtDateButton(): boolean {
    return (this.forceAddCourtTaskButton || this.nextStep?.show_new_court_date_form) && !this.nextStep?.new_court_date_required;
  }

  addCourtDateForm() {
    let taskName = 'Court Appearance';
    if (this.hasJudgementForm) {
      taskName = 'Followup ' + taskName;
    }
    this.form.addControl('new_court_task', generateCourtTaskForm(this.fb, null, taskName));
    this.hasCourtDateForm = true;
  }

  removeCourtDateForm() {
    this.form.removeControl('new_court_task');
    this.hasCourtDateForm = false;
  }

  public save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.message = 'Please fix the errors in the form to complete the step.';
      this.snackBar.open(this.message, 'Dismiss', {
        duration: 3000
      });
      return;
    } else if (this.caseTasks?.length && this.caseTasks.some(task => task.type === TaskType.CASE_STEP_TASK && task.required && !task.marked_completed_date)) {
      this.message = 'Please complete all required tasks before completing the step.';
      this.snackBar.open(this.message, 'Dismiss', {
        duration: 3000
      });
      return;
    }

    // ToStepCompletionPayload owns the wire conversions (MULTI_SELECT join, $0 billable filter) so
    // that this path and the two parentForm hosts that never call save() cannot drift apart — see
    // its doc comment. It returns a new object, leaving the form's controls holding their arrays.
    const value = ToStepCompletionPayload(this.form.value as StepCompletionModel);
    value.case_id = this.caseId;
    value.step = this.nextStep?.step;
    this.onSave.emit(value);
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.formChangesSub?.unsubscribe();
  }

  editTask(task: BaseTask) {
    const dialogRef = this.dialog.open(CaseTaskEditorDialogComponent, {
      data: { editorType: CaseTaskEditorType.TaskEdit, taskDetails: task, type: task.type, userSuggestions: this.userSuggestions },
      width: '500px',
      maxWidth: '500px',
      disableClose: false
    });
    this.subs.push(
      dialogRef.afterClosed().subscribe((result: {form: UntypedFormGroup | undefined,  operation: CaseTaskOperation}) => {
        if (result?.operation === CaseTaskOperation.Edit) {
          this.updatingTaskList = true;
          this.onUpdateTask.emit(result.form.value);
        } else if (result?.operation === CaseTaskOperation.Delete) {
          this.updatingTaskList = true;
          this.onDeleteTask.emit(task);
        }
      })
    );
  }

  taskDrop(event: CdkDragDrop<string[]>) {
    this.updatingTaskList = true;
    // generate new order values based on the new order and push to the event
    const newOrder: { [key: string]: number } = {};

    // update the order of the tasks
    this.caseTasks?.forEach((task, index) => {
      if (index === event.previousIndex) {
        newOrder[task.id] = this.caseTasks[event.currentIndex].order;
      } else if (index >= event.currentIndex && index < event.previousIndex) {
        newOrder[task.id] = this.caseTasks[index + 1].order;
      } else if (index <= event.currentIndex && index > event.previousIndex) {
        newOrder[task.id] = this.caseTasks[index - 1].order;
      } else {
        newOrder[task.id] = task.order;
      }
    });

    this.onUpdateTaskOrder.emit(newOrder);
  }

  async replaceCountyBillableItem(nextStep: WorkflowStep, county: County) {
    const countyFeeTypes = [FlatFeeType.COUNTY_FEE, FlatFeeType.WRIT_FEE, FlatFeeType.SHERIFF_FEE, FlatFeeType.HEARING_FEE];
    const countyCategories = ['builtin-county-fee', 'builtin-writ-filing-fee', 'builtin-sheriff-fee', 'builtin-hearing-fee'];

    // remove existing county-based billable items
    for (let i = this.billableItemsArray.controls.length - 1; i >= 0; i--) {
      const feeType = this.billableItemsArray.controls[i].get('flat_fee_type')?.value;
      const categoryId = this.billableItemsArray.controls[i].get('category_id')?.value;
      if (countyFeeTypes.includes(feeType) || countyCategories.includes(categoryId)) {
        this.billableItemsArray.removeAt(i);
      }
    }

    // check which county-based fee types the workflow step requires and re-add them
    const countyFeeCount = nextStep?.billable_items.filter((item) => item.flat_fee_type === FlatFeeType.COUNTY_FEE || item.category_id === 'builtin-county-fee').length ?? 0;
    const writFeeCount = nextStep?.billable_items.filter((item) => item.flat_fee_type === FlatFeeType.WRIT_FEE || item.category_id === 'builtin-writ-filing-fee').length ?? 0;
    const sheriffFeeCount = nextStep?.billable_items.filter((item) => item.flat_fee_type === FlatFeeType.SHERIFF_FEE || item.category_id === 'builtin-sheriff-fee').length ?? 0;
    const hearingFeeCount = nextStep?.billable_items.filter((item) => item.flat_fee_type === FlatFeeType.HEARING_FEE || item.category_id === 'builtin-hearing-fee').length ?? 0;

    if (countyFeeCount || writFeeCount || sheriffFeeCount || hearingFeeCount) {
      try {
        this.countyFeeSetting = await lastValueFrom(this.countyFeeSettingsService.getByCounty(county));
      } catch {
        return; // fee lookup failed — leave the (removed) county billables for the next refresh
      }
      if (this.countyFeeSetting) {
        for (let i = 0; i < countyFeeCount; i++) {
          this.billableItemsArray.push(generateCountyFeeBillable(this.fb, this.countyFeeSetting, this.county, this.tenants));
        }
        for (let i = 0; i < writFeeCount; i++) {
          this.billableItemsArray.push(generateWritFeeBillable(this.fb, this.countyFeeSetting, this.county, this.tenants));
        }
        for (let i = 0; i < sheriffFeeCount; i++) {
          this.billableItemsArray.push(generateSheriffFeeBillable(this.fb, this.countyFeeSetting, this.county, this.tenants));
        }
        for (let i = 0; i < hearingFeeCount; i++) {
          this.billableItemsArray.push(generateHearingFeeBillable(this.fb, this.countyFeeSetting, this.county, this.tenants));
        }
      }
    }
  }

}
